import React, { useEffect, useMemo, useState } from "react";
import { Page, Document, pdfjs } from "react-pdf";
import { useTranslation } from "react-i18next";
import {
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
} from "@mui/icons-material";
import { CircularProgress, Stack, IconButton } from "@mui/material";

const PDF_MAX_ZOOM = 2.5;
const PDF_MIN_ZOOM = 0.4;

const PdfDocumentWithZoom = ({ url }) => {
  const { t } = useTranslation();

  useEffect(() => {
    pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;
  }, []);

  const [numPages, setNumPages] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null); // Track PDF URL
  const [parentWidth, setParentWidth] = useState(null); // Track parent component width
  const [zoom, setZoom] = useState(1); // Track zoom level

  useEffect(() => {
    setPdfUrl(url);
  }, [url]);

  useEffect(() => {
    // Update parent width when component is mounted or resized
    const handleResize = () => {
      const parentDiv = document.getElementById("pdf-parent");
      if (parentDiv) {
        setParentWidth(parentDiv.clientWidth);
      }
    };
    window.addEventListener("resize", handleResize);
    handleResize(); // Initial width setup
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (pdfUrl) {
      // Once the PDF URL is available, parse it to get the number of pages
      pdfjs.getDocument(pdfUrl).promise.then((pdf) => {
        setNumPages(pdf.numPages);
      });
    }
  }, [pdfUrl]);

  const handleZoomIn = () => {
    const newZoom = zoom + 0.1;
    if (newZoom <= PDF_MAX_ZOOM) {
      setZoom(newZoom);
    }
  };

  const handleZoomOut = () => {
    const newZoom = zoom - 0.1;
    if (newZoom >= PDF_MIN_ZOOM) {
      setZoom(newZoom);
    }
  };

  const renderPages = useMemo(() => {
    const pages = [];
    for (let i = 1; i <= numPages; i++) {
      pages.push(
        <Page
          key={i}
          pageNumber={i}
          scale={zoom}
          width={parentWidth} // Apply zoom to page width
          renderTextLayer={false}
          renderAnnotationLayer={false}
        />,
      );
    }
    return pages;
  }, [numPages, parentWidth, zoom]);

  return (
    <div
      id="pdf-parent"
      style={{
        width: "100%",
        height: "100%",
        position: "relative",
        overflowX: "hidden",
      }}
    >
      {pdfUrl && (
        <Stack>
          <Stack
            direction="row"
            spacing={1}
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 9999,
              backgroundColor: "#424242",
              height: 52,
              justifyContent: "flex-end",
              alignItems: "center",
              paddingRight: 2,
            }}
          >
            {numPages && (
              <>
                <p style={{ color: "white", margin: "0 auto 0 2em" }}>
                  {t("pages.pdfDocument.text01")}: {numPages}
                </p>

                <IconButton
                  style={{ color: "white", border: 1 }}
                  onClick={handleZoomIn}
                >
                  <ZoomInIcon />
                </IconButton>
                <IconButton style={{ color: "white" }} onClick={handleZoomOut}>
                  <ZoomOutIcon />
                </IconButton>
              </>
            )}
          </Stack>
          <div style={{ width: "100%", height: "100%", overflow: "scroll" }}>
            <Document
              file={pdfUrl}
              onLoadError={(error) =>
                console.error("Error loading PDF:", error)
              }
              error={<span>Unable to Load PDF</span>}
              loading={
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <CircularProgress />
                </div>
              }
            >
              {renderPages}
            </Document>
          </div>
        </Stack>
      )}
    </div>
  );
};

export default PdfDocumentWithZoom;
